import { computed, Injectable, inject, signal } from '@angular/core';
import { GeotechnicalAnalysisReport, GeotechnicalAnalysisService, IGeotechnicalFormulaDashboard, FORMULA_DASHBOARD_STATUS, IGeotechnicalValueEngineering, IGeotechnicalVendorAnalysis } from './geotechnical-analysis.service';
export type { IGeotechnicalVendorAnalysis };

export interface GeoTechnicalAnalysisReportData {
    parent?: unknown;
    analysisReportId: string;
    projectId: string;
    analysisReport: GeotechnicalAnalysisReport | null;
    emptyDocument: boolean;
    currentRole: string;
    isOwner: boolean;
    isEditor: boolean;
    permissions: {
        canView: boolean;
        canEdit: boolean;
        canComment: boolean;
        restricted: boolean;
    };
}

@Injectable({
    providedIn: 'root'
})
export class GeoTechnicalAnalysisReportDataService {
    private geotechnicalAnalysisService = inject(GeotechnicalAnalysisService);

    private dataSignal = signal<GeoTechnicalAnalysisReportData | null>(null);

    readonly data = this.dataSignal.asReadonly();
    readonly analysisReportId = computed(() => this.dataSignal()?.analysisReportId || '');
    readonly projectId = computed(() => this.dataSignal()?.projectId || '');
    readonly analysisReport = computed(() => this.dataSignal()?.analysisReport ?? null);
    readonly emptyDocument = computed(() => this.dataSignal()?.emptyDocument ?? true);
    readonly currentRole = computed(() => this.dataSignal()?.currentRole || '');
    readonly isOwner = computed(() => this.dataSignal()?.isOwner ?? false);
    readonly isEditor = computed(() => this.dataSignal()?.isEditor ?? false);
    readonly permissions = computed(() => this.dataSignal()?.permissions);

    private setupModeSignal = signal<boolean>(false);
    readonly setupMode = this.setupModeSignal.asReadonly();

    private valueEngineeringSignal = signal<IGeotechnicalValueEngineering | null>(null);
    readonly valueEngineering = this.valueEngineeringSignal.asReadonly();
    private valueEngineeringLoadingSignal = signal<boolean>(false);
    readonly valueEngineeringLoading = this.valueEngineeringLoadingSignal.asReadonly();

    private vendorAnalysisSignal = signal<IGeotechnicalVendorAnalysis | null>(null);
    readonly vendorAnalysis = this.vendorAnalysisSignal.asReadonly();

    private formulaDashboardSignal = signal<IGeotechnicalFormulaDashboard | null>(null);
    readonly formulaDashboard = this.formulaDashboardSignal.asReadonly();

    private formulaDashboardLoadingSignal = signal<boolean>(false);
    readonly formulaDashboardLoading = this.formulaDashboardLoadingSignal.asReadonly();

    private formulaDashboardErrorSignal = signal<string>('');
    readonly formulaDashboardError = this.formulaDashboardErrorSignal.asReadonly();

    loadValueEngineering(): void {
        const analysisId = this.analysisReportId();
        if (!analysisId) return;
        this.valueEngineeringLoadingSignal.set(true);
        this.geotechnicalAnalysisService.getValueEngineering(analysisId).subscribe({
            next: (res) => {
                this.valueEngineeringSignal.set(res.success ? (res.data ?? null) : null);
                this.valueEngineeringLoadingSignal.set(false);
            },
            error: () => { this.valueEngineeringLoadingSignal.set(false); },
        });
    }

    setVendorAnalysis(data: IGeotechnicalVendorAnalysis | null): void {
        this.vendorAnalysisSignal.set(data);
    }

    setSetupMode(value: boolean): void {
        this.setupModeSignal.set(value);
    }

    setFormulaDashboard(formulaDashboard: IGeotechnicalFormulaDashboard | null): void {
        this.formulaDashboardSignal.set(formulaDashboard);
    }

    setFormulaDashboardFailed(error?: string): void {
        this.formulaDashboardSignal.set({
            geotechnicalAnalysis: this.analysisReportId(),
            status: FORMULA_DASHBOARD_STATUS.FAILED,
            error: error ?? null,
            sections: [],
            parameterValues: [],
        } as unknown as IGeotechnicalFormulaDashboard);
        this.formulaDashboardLoadingSignal.set(false);
    }

    loadFormulas(): void {
        const analysisId = this.analysisReportId();
        if (!analysisId) {
            this.formulaDashboardSignal.set(null);
            this.formulaDashboardLoadingSignal.set(false);
            this.formulaDashboardErrorSignal.set('Geotechnical analysis not found');
            return;
        }

        this.formulaDashboardLoadingSignal.set(true);
        this.formulaDashboardErrorSignal.set('');
        this.formulaDashboardSignal.set(null);

        this.geotechnicalAnalysisService.getFormulaDashboard(analysisId).subscribe({
            next: (res) => {
                if (res.success) {
                    this.formulaDashboardSignal.set(res.data ?? null);
                    this.formulaDashboardErrorSignal.set('');
                } else {
                    this.formulaDashboardSignal.set(null);
                    this.formulaDashboardErrorSignal.set(res.message ?? 'Failed to load formula dashboard');
                }
                this.formulaDashboardLoadingSignal.set(false);
            },
            error: (error) => {
                this.formulaDashboardSignal.set(null);
                this.formulaDashboardErrorSignal.set(
                    error instanceof Error
                        ? error.message
                        : typeof error === 'string'
                            ? error
                            : 'Failed to load formula dashboard'
                );
                this.formulaDashboardLoadingSignal.set(false);
            },
        });
    }

    setData(data: GeoTechnicalAnalysisReportData | null) {
        this.dataSignal.set(data);
        if (!data) {
            this.valueEngineeringSignal.set(null);
            this.valueEngineeringLoadingSignal.set(false);
            this.vendorAnalysisSignal.set(null);
            this.formulaDashboardSignal.set(null);
            this.formulaDashboardLoadingSignal.set(false);
            this.formulaDashboardErrorSignal.set('');
        }
    }

    updateData(partialData: Partial<GeoTechnicalAnalysisReportData>) {
        const currentData = this.dataSignal();
        if (currentData) {
            this.dataSignal.set({ ...currentData, ...partialData });
        }
    }

    getData() {
        return this.dataSignal();
    }

    refreshAnalysisExcelUrl(): void {
        const analysisId = this.analysisReportId();
        if (!analysisId) return;

        this.geotechnicalAnalysisService.getAnalysisFile(analysisId).subscribe({
            next: (res) => {
                const newUrl = res.data?.awsSignedUrl;
                if (!newUrl) return;
                const current = this.dataSignal();
                if (!current?.analysisReport) return;
                this.dataSignal.set({
                    ...current,
                    analysisReport: { ...current.analysisReport, analysisExcelUrl: newUrl },
                });
            },
        });
    }
}
